import { Col, Create, Divider, Form, Input, Row, SaveButton, Select, useForm, useSelect } from "@pankod/refine-antd";
import { IResourceComponentsProps, useDataProvider, useOne } from "@pankod/refine-core";
import { DatePicker } from "antd";
import type { Rule } from "antd/es/form";
import { CancelButton } from "components/utils/cancelButton";
import dayjs from "dayjs";
import { ILocations, ILookup, ISawAsset, ISawAssetMetadata } from "interfaces";
import { useMemo, useRef, useState } from "react";
import { DATAPROVIDER_CREATE, DATAPROVIDER_LOOKUP, RESOURCE_PATH, STALE_DURATION, buildAttachmentUrl, getGUID } from "scripts/site";
import { EditSawAssetConfig } from "../asset2/editAsset2Config";

export const SawAssetCreate: React.FC<IResourceComponentsProps> = () => {
	const { formProps, saveButtonProps, form } = useForm<ISawAsset>({
		dataProviderName: DATAPROVIDER_CREATE,
		redirect: "list",
	});

	const { onFinish, ...restFormProps } = formProps;
	const dataProviderFactory = useDataProvider();

	const { data: metadata } = useOne<ISawAssetMetadata>({
		dataProviderName: DATAPROVIDER_LOOKUP,
		resource: RESOURCE_PATH.METADATA,
		id: RESOURCE_PATH.SAW_ASSET,
		queryOptions: {
			enabled: true,
			staleTime: STALE_DURATION,
		},
	});

	const metaConfig = metadata?.data.config;
	const metaFields = metadata?.data.fields;

	const serialNumberUrl = buildAttachmentUrl(
		RESOURCE_PATH.METADATA,
		RESOURCE_PATH.SAW_ASSET,
		metaFields?.find((field) => field.name === "serialNumber")?.attachment,
	);

	const accessoriesOptions = (metaConfig?.accessories ?? []).filter((item): item is string => Boolean(item));
	const makeModelOptions = (metaConfig?.makeModel ?? []).filter((item): item is string => Boolean(item));
	const resolutionOptions = (metaConfig?.resolution ?? []).filter((item): item is string => Boolean(item));

	const [selectedCountry, setSelectedCountry] = useState<string | undefined>(
		() => (restFormProps.initialValues?.countryId as string | undefined) ?? undefined,
	);

	const guidRef = useRef<string>(getGUID());
	const initialFormValues = useRef({ id: guidRef.current }).current;

	const { selectProps: countryProps } = useSelect<ILookup>({
		dataProviderName: DATAPROVIDER_LOOKUP,
		resource: RESOURCE_PATH.COUNTRY,
		optionLabel: "name",
		optionValue: "id",
		sort: [{ field: "name", order: "asc" }],
		fetchSize: 100,
		filters: [
			{ field: "sawAsset.enabled", operator: "eq", value: true },
		],
	});

	const { queryResult: allLocationsQuery } = useSelect<ILocations>({
		dataProviderName: DATAPROVIDER_LOOKUP,
		resource: RESOURCE_PATH.LOCATION,
		optionLabel: "name",
		optionValue: "id",
		sort: [{ field: "name", order: "asc" }],
		fetchSize: 100,
	});

	const allLocationsData = allLocationsQuery.data?.data;

	const locationOptions = useMemo(() => {
		const availableLocations = allLocationsData ?? [];
		return availableLocations
			.map((location) => ({
				label: location.name,
				value: location.id,
				countryId: location.countryId,
			}))
			.sort((a, b) => a.label.localeCompare(b.label));
	}, [allLocationsData]);

	const countryScopedLocations = useMemo(
		() =>
			selectedCountry
				? locationOptions.filter((option) => option.countryId === selectedCountry)
				: locationOptions,
		[locationOptions, selectedCountry],
	);

	const { selectProps: msInventorySelectProps } = useSelect<ILocations>({
		dataProviderName: DATAPROVIDER_LOOKUP,
		resource: RESOURCE_PATH.LOCATION,
		optionLabel: "name",
		optionValue: "id",
		filters: [
			{ field: "countryId", operator: "eq", value: selectedCountry },
			{ field: "sawasset.enabled", operator: "eq", value: true },
		],
		sort: [{ field: "name", order: "asc" }],
		fetchSize: 100,
		queryOptions: { enabled: Boolean(selectedCountry) },
	});

	const msInventoryOptions = (msInventorySelectProps.options ?? []) as { label: string; value: string }[];

	const filterOptionByLabel = (input: string, option?: { label?: any }) => {
		const label = option?.label;
		if (typeof label === "string") {
			return label.toLowerCase().includes(input.toLowerCase());
		}
		if (typeof label === "number") {
			return label.toString().includes(input);
		}
		return false;
	};

	const twoColumnLayout = {
		xs: { span: 24 },
		sm: { span: 24 },
		md: { span: 12 },
		lg: { span: 12 },
		xl: { span: 12 },
		xxl: { span: 12 },
	};

	const rowGutter = { xs: 8, sm: 16, md: 24, lg: 32, xl: 40, xxl: 48 };

	const validateSawAssetId = async (_: unknown, value: string) => {
		if (!value) {
			return Promise.resolve();
		}

		try {
			const dataProvider = dataProviderFactory(DATAPROVIDER_LOOKUP);
			const existing = await dataProvider.getList<ISawAsset>({
				resource: RESOURCE_PATH.SAW_ASSET,
				pagination: { current: 1, pageSize: 1 },
				filters: [{ field: "sawAssetId", operator: "eq", value: "numtostring-" + value }],
				sort: [{ field: "updatedAt", order: "desc" }],
			});
			if ((existing?.data ?? []).length > 0) {
				return Promise.reject(new Error("'SAW Asset ID' already exists."));
			}
			return Promise.resolve();
		} catch {
			return Promise.reject(new Error("Unable to validate SAW Asset ID. Please retry."));
		}
	};

	const handleFinish = (values: Record<string, unknown>) => {
		const typedValues = values as unknown as ISawAsset;
		const coerceNumber = (value: unknown) => {
			if (value === undefined || value === null || value === "") {
				return undefined;
			}
			const parsed = Number(value);
			return Number.isFinite(parsed) ? parsed : undefined;
		};
		const payload: ISawAsset = {
			...typedValues,
			stateAction: typedValues.stateAction,
			poDetails: coerceNumber(typedValues.poDetails) as ISawAsset["poDetails"],
			deviceValue: coerceNumber(typedValues.deviceValue) as ISawAsset["deviceValue"],
			dateOfDelivery: typedValues.dateOfDelivery ? dayjs(typedValues.dateOfDelivery).toISOString() : undefined,
		};

		void onFinish?.(payload);
	};

	const optionalPoDetailsRule: Rule = {
		validator: (_, value) => {
			if (value === undefined || value === null) {
				return Promise.resolve();
			}
			const normalized = String(value).trim();
			if (!normalized) {
				return Promise.resolve();
			}
			if (normalized.length < 5 || normalized.length > 20) {
				return Promise.reject(new Error("PO Details must be between 5 and 20 digits"));
			}
			if (!/^[0-9]+$/.test(normalized)) {
				return Promise.reject(new Error("PO Details must be numeric only"));
			}
			return Promise.resolve();
		},
	};

	const optionalNumericValueRule: Rule = {
		validator: (_, value) => {
			if (value === undefined || value === null) {
				return Promise.resolve();
			}
			const normalized = String(value).trim();
			if (!normalized) {
				return Promise.resolve();
			}
			if (!/^[0-9]+$/.test(normalized)) {
				return Promise.reject(new Error("Value must be numbers only"));
			}
			return Promise.resolve();
		},
	};

	return (
		<Create
			title="Add a SAW Asset"
			footerButtons={() => (
				<>
					<SaveButton {...saveButtonProps} />
					<CancelButton />
				</>
			)}
		>
			<Form
				{...restFormProps}
				initialValues={{ ...(restFormProps.initialValues ?? {}), ...initialFormValues }}
				autoComplete="off"
				layout="vertical"
				onFinish={handleFinish}
			>
				<Divider orientation="left">Asset Information</Divider>
				<Row gutter={rowGutter}>
					<Col className="gutter-row" {...twoColumnLayout}>
						<Form.Item
							label="SAW Asset ID"
							name="sawAssetId"
							rules={[
								{
									required: true,
									type: "string",
									whitespace: true,
									max: 20,
									min: 5,
								},
								{
									pattern: /^[a-zA-Z0-9]+$/,
									message: "Asset ID must be alphanumeric only (no spaces or special characters)",
								},
								{
									validator: validateSawAssetId,
								},
							]}
						>
							<Input placeholder="Enter Saw asset ID" />
						</Form.Item>
						<Form.Item name="id" hidden>
							<Input />
						</Form.Item>
					</Col>
					<Col className="gutter-row" {...twoColumnLayout}>
						<Form.Item
							tooltip={(
								<>
									Launch Windows PowerShell and execute the command below to identify the computer serial number:
									<br />
									<strong>Get-WmiObject Win32_BIOS | Select -Expand SerialNumber</strong>
									{serialNumberUrl && (
										<div>
											<a href={serialNumberUrl} rel="noreferrer" target="_blank">
												View detailed steps
											</a>
										</div>
									)}
								</>
							)}
							label="Serial Number"
							name="serialNumber"
							rules={[
								{
									required: true,
									type: "string",
									whitespace: true,
									min: 5,
									max: 20,
								},
							]}
						>
							<Input />
						</Form.Item>
					</Col>
				</Row>
				<Row gutter={rowGutter}>
					<Col className="gutter-row" {...twoColumnLayout}>
						<Form.Item
							label="PO Details"
							name="poDetails"
							rules={[optionalPoDetailsRule]}
						>
							<Input placeholder="Enter PO Number" />
						</Form.Item>
					</Col>
					<Col className="gutter-row" {...twoColumnLayout}>
						<Form.Item
							label="Date of Delivery"
							name="dateOfDelivery"
							tooltip="Asset delivery date (cannot be in the future or more than 6 months ago)"
							rules={[{ type: "date", message: "Please select Date of Delivery" }]}
						>
							<DatePicker
								picker="date"
								style={{ width: "100%" }}
								disabledDate={(date) => date && (date > dayjs() || date < dayjs().subtract(6, "months"))}
							/>
						</Form.Item>
					</Col>
				</Row>
				<Row gutter={rowGutter}>
					<Col className="gutter-row" {...twoColumnLayout}>
						<Form.Item
							label="Value of SAW Asset"
							name="deviceValue"
							rules={[optionalNumericValueRule]}
						>
							<Input placeholder="Enter Value (numbers only)" />
						</Form.Item>
					</Col>
					<Col className="gutter-row" {...twoColumnLayout}>
						<Form.Item
							label="Dock Serial Number"
							name="dockSerialNumber"
							rules={[
								{
									required: false,
									type: "string",
									whitespace: true,
									min: 5,
									max: 20,
								},
								{
									pattern: /^[a-zA-Z0-9]+$/,
									message: "Dock Serial Number must be alphanumeric only",
								},
							]}
						>
							<Input placeholder="Enter dock serial number (optional)" />
						</Form.Item>
					</Col>
				</Row>
				{accessoriesOptions.length > 0 && (
					<Row gutter={rowGutter}>
						<Col className="gutter-row" {...twoColumnLayout}>
							<Form.Item
								label="Accessories"
								name="accessories"
								tooltip="Select all accessories provided with the asset."
							>
								<Select
									mode="multiple"
									allowClear
									placeholder="Select Accessories"
									options={accessoriesOptions.map((accessory) => ({ label: accessory, value: accessory }))}
								/>
							</Form.Item>
						</Col>
						<Col className="gutter-row" {...twoColumnLayout}>
							{(makeModelOptions.length > 0 || resolutionOptions.length > 0) && (
								<EditSawAssetConfig
									makeModel={makeModelOptions}
									resolution={resolutionOptions}
								// fields={metaFields} // dead code: reserved for planned metadata-driven UI
								/>
							)}
						</Col>
					</Row>
				)}
				{accessoriesOptions.length === 0 && (makeModelOptions.length > 0 || resolutionOptions.length > 0) && (
					<EditSawAssetConfig
						makeModel={makeModelOptions}
						resolution={resolutionOptions}
					// fields={metaFields} // dead code: reserved for planned metadata-driven UI
					/>
				)}
				<Divider orientation="left">Location</Divider>
				<Row gutter={rowGutter}>
					<Col className="gutter-row" {...twoColumnLayout}>
						<Form.Item
							label="Country"
							name="countryId"
							rules={[
								{
									required: true,
									whitespace: true,
								},
							]}
						>
							<Select
								allowClear
								placeholder="Select Country"
								showSearch
								options={countryProps.options}
								loading={countryProps.loading}
								onChange={(value) => {
									setSelectedCountry(value as string | undefined);
									form.setFieldsValue({
										msInventoryLocation: undefined,
										tcsBranch: undefined,
									});
								}}
							/>
						</Form.Item>
					</Col>
					<Col className="gutter-row" {...twoColumnLayout}>
						<Form.Item
							label="MS Inventory Location"
							name="msInventoryLocation"
							rules={[{ required: true, type: "string" }]}
						>
							<Select
								{...msInventorySelectProps}
								allowClear
								placeholder={selectedCountry ? "Select MS Inventory Location" : "Select Country first"}
								showSearch
								disabled={!selectedCountry}
								optionFilterProp="label"
								filterOption={filterOptionByLabel}
								notFoundContent={selectedCountry ? "No MS inventory locations available" : undefined}
								options={msInventoryOptions}
							/>
						</Form.Item>
					</Col>
				</Row>

				<Row gutter={rowGutter}>
					<Col className="gutter-row" {...twoColumnLayout}>
						<Form.Item
							label="TCS Branch"
							name="tcsBranch"
							rules={[
								{
									required: true,
									type: "string",
								},
							]}
						>
							<Select
								allowClear
								placeholder="Select TCS Branch"
								showSearch
								optionFilterProp="label"
								filterOption={filterOptionByLabel}
								notFoundContent={selectedCountry ? "No branches for this country" : undefined}
								options={countryScopedLocations.map(({ label, value }) => ({ label, value }))}
							/>
						</Form.Item>
					</Col>
				</Row>

				<Form.Item name="name" hidden initialValue="SAW Asset">
					<Input />
				</Form.Item>
			</Form>
		</Create>
	);
};